/**
 * $ref resolution and allOf flattening.
 *
 * Local refs (#/$defs/Foo) are resolved directly against the root schema.
 * Remote refs (http(s)://example.com/schema.json) are resolved against an
 * optional `RefRegistry` — a Map keyed by URL. Callers populate the
 * registry by fetching remote schemas ahead of time (typically once per
 * project open, see apps/web's RemoteRefLoader). Refs whose URL isn't in
 * the registry throw `UnsupportedSchemaError` with a clear message; this
 * lets the editor surface "schema not loaded" rather than crashing.
 *
 * allOf is handled by merging subschemas shallowly. Conflicts (e.g. two
 * different `type`s) throw — that's a malformed schema, better to fail fast.
 */

import type { JsonSchema } from '@bliksys/shared';

import { RefResolutionError, UnsupportedSchemaError } from './errors.js';
import { parsePointer } from './pointer.js';

/**
 * Map of URL → fetched schema. The URL is the bare URL without any
 * fragment (`https://example.com/schema.json`, NOT
 * `https://example.com/schema.json#/$defs/Foo`). The fragment is
 * resolved against the registered schema after lookup.
 */
export type RefRegistry = ReadonlyMap<string, JsonSchema>;

/**
 * Resolve a schema node: follow $ref (local or remote), merge allOf.
 * Does NOT recurse into nested properties/items — that's the caller's
 * job. This keeps the resolver predictable and avoids exponential
 * blowup on recursive schemas.
 *
 * `registry`, if provided, lets the resolver follow `http(s)://...` refs.
 * Without a registry, remote refs throw `UnsupportedSchemaError`.
 */
export function resolveSchema(
  schema: JsonSchema,
  root: JsonSchema,
  seenRefs: ReadonlySet<string> = new Set(),
  registry?: RefRegistry,
): JsonSchema {
  let current = schema;

  // Follow $ref chains, guarding against cycles.
  while (current.$ref !== undefined) {
    if (seenRefs.has(current.$ref)) {
      throw new RefResolutionError(`cyclic ref: ${current.$ref}`);
    }
    const nextSeen = new Set(seenRefs);
    nextSeen.add(current.$ref);
    current = followRef(current.$ref, root, registry);
    seenRefs = nextSeen;
  }

  // Merge allOf into the current schema. Subschemas themselves may have $refs.
  if (current.allOf && current.allOf.length > 0) {
    const merged: JsonSchema = { ...current };
    delete merged.allOf;
    for (const sub of current.allOf) {
      const resolvedSub = resolveSchema(sub, root, seenRefs, registry);
      mergeInto(merged, resolvedSub);
    }
    current = merged;
  }

  return current;
}

function followRef(ref: string, root: JsonSchema, registry?: RefRegistry): JsonSchema {
  // Local fragment-only ref — resolve against `root`.
  if (ref.startsWith('#')) {
    return followFragment(ref.slice(1), root, ref);
  }
  // Remote ref. Split URL and optional fragment.
  if (ref.startsWith('http://') || ref.startsWith('https://')) {
    const hashIdx = ref.indexOf('#');
    const url = hashIdx === -1 ? ref : ref.slice(0, hashIdx);
    const fragment = hashIdx === -1 ? '' : ref.slice(hashIdx + 1);
    if (!registry) {
      throw new UnsupportedSchemaError(
        `remote $ref ${ref} requires a ref registry; none was provided`,
      );
    }
    const remoteRoot = registry.get(url);
    if (remoteRoot === undefined) {
      throw new UnsupportedSchemaError(`remote $ref ${ref}: schema for ${url} is not loaded yet`);
    }
    return followFragment(fragment, remoteRoot, ref);
  }
  // Anything else (file://, urn:, relative paths) — out of scope.
  throw new UnsupportedSchemaError(`unsupported $ref scheme: ${ref}`);
}

/**
 * Resolve a JSON-pointer fragment against a schema root. Empty fragment
 * means "the root itself". The `originalRef` is included in error
 * messages for clarity.
 */
function followFragment(pointer: string, root: JsonSchema, originalRef: string): JsonSchema {
  if (pointer === '') return root;

  const segments = parsePointer(pointer);
  let node: unknown = root;
  for (const segment of segments) {
    if (node === null || typeof node !== 'object') {
      throw new RefResolutionError(originalRef);
    }
    node = (node as Record<string, unknown>)[segment];
    if (node === undefined) throw new RefResolutionError(originalRef);
  }

  if (typeof node !== 'object' || node === null || Array.isArray(node)) {
    throw new RefResolutionError(originalRef);
  }
  return node;
}

/**
 * Shallow merge `source` into `target`. Conflicts on `type` throw.
 * `properties` and `required` merge structurally rather than replacing.
 */
function mergeInto(target: JsonSchema, source: JsonSchema): void {
  for (const [rawKey, value] of Object.entries(source)) {
    if (value === undefined) continue;
    // Never schema keywords — and assigning `__proto__` below would rewrite
    // the merged object's prototype instead of adding a data key.
    if (rawKey === '__proto__' || rawKey === 'constructor' || rawKey === 'prototype') continue;
    const key = rawKey as keyof JsonSchema;

    if (key === 'properties') {
      target.properties = { ...(target.properties ?? {}), ...(value as JsonSchema['properties']) };
    } else if (key === 'required') {
      const existing = target.required ?? [];
      const incoming = value as string[];
      target.required = Array.from(new Set([...existing, ...incoming]));
    } else if (key === 'type') {
      if (target.type !== undefined && !typesMatch(target.type, value as JsonSchema['type'])) {
        throw new UnsupportedSchemaError(
          `allOf with conflicting types: ${String(target.type)} vs ${String(value)}`,
        );
      }
      target.type = value as NonNullable<JsonSchema['type']>;
    } else {
      (target as Record<string, unknown>)[rawKey] = value;
    }
  }
}

function typesMatch(a: JsonSchema['type'], b: JsonSchema['type']): boolean {
  if (a === b) return true;
  const aArr = Array.isArray(a) ? a : a !== undefined ? [a] : [];
  const bArr = Array.isArray(b) ? b : b !== undefined ? [b] : [];
  return aArr.some((t) => bArr.includes(t));
}
